import React, { useState, useEffect, useMemo } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverAnchor,
} from "@src/lib/ui/Popover";
import { Button } from "@src/lib/ui/Button";
import {
  ChatAttachMenuContent,
  FilterType,
  SelectableItem,
} from "./ChatAttachMenuContent";
import { AdminLibraryInput, LibraryItemType } from "@src/types.generated";
import { useChatAttachMenuLibraryItemsQuery } from "@src/features/assistantChat/aiChat.generated";
import Paperclip1Icon from "@src/lib/icons/18px/paperclip-1";
import { cn } from "@src/lib/utils/cn";

export interface ChatAttachMenuProps {
  onSelectItem: (item: SelectableItem) => void;
  onUploadClick?: () => void;
  excludeLibraryItemIds?: string[];
  disabled?: boolean;
  className?: string;
  /**
   * When provided, the popover will be anchored to the top-left of this element
   * instead of the trigger button, using a virtual anchor.
   */
  anchorElementRef?: React.RefObject<HTMLElement | null>;
}

export const ChatAttachMenu: React.FC<ChatAttachMenuProps> = ({
  onSelectItem,
  onUploadClick,
  excludeLibraryItemIds = [],
  disabled = false,
  className = "",
  anchorElementRef,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<FilterType>(null);
  const [searchValue, setSearchValue] = useState("");

  // Manage a virtual anchor rect derived from the provided anchor element
  const [virtualAnchorRect, setVirtualAnchorRect] = useState<DOMRect | null>(
    null,
  );

  useEffect(() => {
    if (!isOpen) return;
    const updateRect = () => {
      if (anchorElementRef?.current) {
        const rect = anchorElementRef.current.getBoundingClientRect();
        // Anchor to the top-left of the element
        const topLeftRect = new DOMRect(rect.left, rect.top, 1, 1);
        setVirtualAnchorRect(topLeftRect);
      } else {
        setVirtualAnchorRect(null);
      }
    };

    updateRect();

    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    const interval = window.setInterval(updateRect, 100); // account for animations/layout shifts

    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
      window.clearInterval(interval);
    };
  }, [isOpen, anchorElementRef]);

  // Reset state when menu closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFilter(null);
      setSearchValue("");
    }
  }, [isOpen]);

  // Determine what to query based on current state
  const shouldQuery = isOpen && (selectedFilter || searchValue);

  const queryInput: AdminLibraryInput = useMemo(() => {
    const input: AdminLibraryInput = {
      filters: {
        hasEnglishSearchDocument: { value: true },
      },
    };

    if (searchValue) {
      input.search = { value: searchValue };
    }

    if (selectedFilter) {
      if (selectedFilter === "Course") {
        input.filters = {
          ...input.filters,
          itemTypes: { value: [LibraryItemType.Course] },
        };
      } else if (selectedFilter === "Resource") {
        input.filters = {
          ...input.filters,
          itemTypes: { value: [LibraryItemType.TrainingResource] },
        };
      }
    }

    return input;
  }, [selectedFilter, searchValue, excludeLibraryItemIds]);

  const { data, previousData, loading, error } =
    useChatAttachMenuLibraryItemsQuery({
      variables: {
        input: queryInput,
        pagination: { limit: 30 },
      },
      skip: !shouldQuery,
    });
  const _data = previousData || data;

  const selectableItems: SelectableItem[] = useMemo(() => {
    if (!_data?.AdminLibrary?.objects) return [];

    // Filter out excluded library items on the client side
    const filteredItems = _data.AdminLibrary.objects.filter(
      (libraryItem) => !excludeLibraryItemIds.includes(libraryItem.id),
    );

    return filteredItems.map((libraryItem) => ({
      libraryItem,
    }));
  }, [_data, excludeLibraryItemIds]);

  const handleSelectItem = (item: SelectableItem) => {
    onSelectItem(item);
  };
  const _onUploadClick = () => {
    setIsOpen(false);
    if (onUploadClick) {
      onUploadClick();
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal>
      {virtualAnchorRect ? (
        // Use a virtual anchor to position the popover at the top of the attachments toolbar
        // Note: virtualRef is not in the public types; cast to any to satisfy TS
        <PopoverAnchor
          {...({
            virtualRef: {
              current: { getBoundingClientRect: () => virtualAnchorRect },
            },
          } as any)}
        />
      ) : null}
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="xs"
          disabled={disabled}
          className={cn("h-[28px]", className)}
        >
          <Paperclip1Icon className="text-gray-8 size-3.5" />
          Add context
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        className="p-0 w-[400px]"
      >
        <ChatAttachMenuContent
          selectableItems={selectableItems}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          onSelectItem={handleSelectItem}
          onUploadClick={_onUploadClick}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          isLoading={loading}
        />
      </PopoverContent>
    </Popover>
  );
};
